import type { MedicationPadAlertError } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import { keepDispenseLimitsWithRows } from '@/utils/dosageLimit/dispenseLimitsByFormulation';
import type { DispenseLimitRow } from '@/utils/dosageLimit/getFormulationDispenseLimitRows';
import { getFormulationDispenseLimitRows } from '@/utils/dosageLimit/getFormulationDispenseLimitRows';
import type { DispenseLimitPad, PrescriptionPadRules } from '../types';
import { getMedicationPadItemAlertError } from './getMedicationPadItemAlertError';

const isSameAlert = (current: MedicationPadAlertError | undefined, next: MedicationPadAlertError) =>
  current === next || (!!current && !!next && current.title === next.title && current.description === next.description);

/**
 * The pad re-derived after any action that can move a formulation total, the set limits or the rules: the rows once,
 * every item's alert against them, and the set limits of only the formulations that still have a row.
 * @param derivedRows - the pad's rows when the caller already derived them for the same items and limits
 */
export const revalidatePad = (
  pad: DispenseLimitPad,
  rules: PrescriptionPadRules,
  derivedRows?: DispenseLimitRow[],
): DispenseLimitPad => {
  // No row while dispense limits are off, so a kill switch drops every set limit rather than keep it to re-apply later
  const dispenseLimitRows = rules.enableDispenseLimits
    ? derivedRows ?? getFormulationDispenseLimitRows(pad.medicationPadItems, pad.dispenseLimits)
    : [];
  return {
    medicationPadItems: pad.medicationPadItems.map((item) => {
      const alertError = getMedicationPadItemAlertError(item, dispenseLimitRows, rules);
      // An item whose alert is unchanged stays the same object, so its editor does not re-render
      return isSameAlert(item.alertError, alertError) ? item : { ...item, alertError };
    }),
    // Dropping the limits of formulations left without a row leaves the rows themselves unchanged
    dispenseLimits: keepDispenseLimitsWithRows(pad.dispenseLimits, dispenseLimitRows),
  };
};
